import React, { useState, useEffect } from 'react';

const ModerationProfileForm = ({
	profile,
	models,
	onSubmit,
	onReset,
	onDeleteFile,
}) => {
	const [title, setTitle] = useState('');
	const [model, setModel] = useState('');
	const [temperature, setTemperature] = useState(0.7);
	const [maxTokens, setMaxTokens] = useState(1000);
	const [prompt, setPrompt] = useState('');
	const [interveneAfterMinutes, setInterveneAfterMinutes] = useState(0);
	const [interveneAfterSeconds, setInterveneAfterSeconds] = useState(0);
	const [
		promptIdleParticipantsAfterMinutes,
		setPromptIdleParticipantsAfterMinutes,
	] = useState(0);
	const [
		promptIdleParticipantsAfterSeconds,
		setPromptIdleParticipantsAfterSeconds,
	] = useState(0);
	const [privacyPolicyMessage, setPrivacyPolicyMessage] = useState('');
	const [privacyPolicyLink, setPrivacyPolicyLink] = useState('');
	const [datetime, setDatetime] = useState('');
	const [documents, setDocuments] = useState(null);
	const [associatedDocuments, setAssociatedDocuments] = useState([]);
	const [profileId, setProfileId] = useState('');
	const [formTitle, setFormTitle] = useState('Create Moderation Profile');

	useEffect(() => {
		if (profile) {
			setTitle(profile.title || '');
			setModel(profile.model || '');
			setTemperature(profile.temperature || 0.7);
			setMaxTokens(profile.max_tokens || 1000);
			setPrompt(profile.prompt || '');

			// Convert seconds to minutes and seconds
			const interveneAfter = profile.intervene_after || 0;
			setInterveneAfterMinutes(Math.floor(interveneAfter / 60));
			setInterveneAfterSeconds(interveneAfter % 60);

			const promptIdleAfter = profile.prompt_idle_participants_after || 0;
			setPromptIdleParticipantsAfterMinutes(Math.floor(promptIdleAfter / 60));
			setPromptIdleParticipantsAfterSeconds(promptIdleAfter % 60);

			setPrivacyPolicyMessage(profile.privacy_policy_message || '');
			setPrivacyPolicyLink(profile.privacy_policy_link || '');
			setDatetime(profile.date_time || new Date().toISOString().slice(0, 16));
			setAssociatedDocuments(profile.associated_documents || []);
			setProfileId(profile.id || '');
			setFormTitle('Edit Moderation Profile');
		} else {
			setTitle('');
			setModel('');
			setTemperature(0.7);
			setMaxTokens(1000);
			setPrompt('');
			setInterveneAfterMinutes(0);
			setInterveneAfterSeconds(0);
			setPromptIdleParticipantsAfterMinutes(0);
			setPromptIdleParticipantsAfterSeconds(0);
			setPrivacyPolicyMessage('');
			setPrivacyPolicyLink('');
			setDatetime(new Date().toISOString().slice(0, 16));
			setAssociatedDocuments([]);
			setProfileId('');
			setFormTitle('Create Moderation Profile');
		}
	}, [profile]);

	const handleSubmit = (e) => {
		e.preventDefault();

		// Calculate total seconds for intervene and prompt idle timers
		const interveneAfter =
			(parseInt(interveneAfterMinutes) || 0) * 60 +
			(parseInt(interveneAfterSeconds) || 0);
		const promptIdleParticipantsAfter =
			(parseInt(promptIdleParticipantsAfterMinutes) || 0) * 60 +
			(parseInt(promptIdleParticipantsAfterSeconds) || 0);

		const formData = {
			id: profileId || undefined,
			title,
			model,
			temperature,
			max_tokens: maxTokens,
			prompt,
			intervene_after: interveneAfter,
			prompt_idle_participants_after: promptIdleParticipantsAfter,
			privacy_policy_message: privacyPolicyMessage,
			privacy_policy_link: privacyPolicyLink,
			date_time: datetime,
			documents: documents,
		};

		onSubmit(formData);
	};

	const handleReset = () => {
		setTitle('');
		setModel('');
		setTemperature(0.7);
		setMaxTokens(1000);
		setPrompt('');
		setInterveneAfterMinutes(0);
		setInterveneAfterSeconds(0);
		setPromptIdleParticipantsAfterMinutes(0);
		setPromptIdleParticipantsAfterSeconds(0);
		setPrivacyPolicyMessage('');
		setPrivacyPolicyLink('');
		setDatetime(new Date().toISOString().slice(0, 16));
		setDocuments(null);
		setAssociatedDocuments([]);
		setProfileId('');
		setFormTitle('Create Moderation Profile');

		if (onReset) {
			onReset();
		}
	};

	const handleFileChange = (e) => {
		const files = e.target.files;

		// Validate file extensions
		for (let i = 0; i < files.length; i++) {
			const extension = files[i].name.split('.').pop().toLowerCase();
			if (
				!['csv', 'doc', 'docx', 'txt', 'pdf', 'xls', 'xlsx'].includes(extension)
			) {
				alert('Only CSV, DOC, DOCX, TXT, PDF, XLS, XLSX files are allowed');
				e.target.value = null; // Clear the file input
				return;
			}
		}

		setDocuments(files);
	};

	const handleDeleteDocument = async (filename) => {
		if (onDeleteFile && profileId) {
			await onDeleteFile(profileId, filename);
		}
	};

	return (
		<form id="moderationForm" onSubmit={handleSubmit}>
			<h5 className="mb-3" id="moderationFormTitle">
				{formTitle}
			</h5>
			<input type="hidden" id="profile-id" value={profileId} />

			<div className="form-group">
				<label htmlFor="title">Title:</label>
				<input
					type="text"
					id="title"
					className="form-control"
					value={title}
					onChange={(e) => setTitle(e.target.value)}
					required
				/>
			</div>

			<div className="form-group">
				<label htmlFor="model">Model:</label>
				<select
					className="form-control"
					id="model"
					value={model}
					onChange={(e) => setModel(e.target.value)}
					required
				>
					<option value="">-- Select a model --</option>
					{models.map((modelOption, idx) => (
						<option key={idx} value={modelOption}>
							{modelOption}
						</option>
					))}
				</select>
				<p></p>
				<small>
					Note: Some models (especially older ones) may not be supported. Try
					the newer versions of gpt-4o or gpt-4o-mini.
				</small>
			</div>

			<div className="form-group">
				<label htmlFor="temperature">Temperature:</label>
				<input
					type="number"
					id="temperature"
					step="0.01"
					max="2"
					min="0"
					className="form-control"
					value={temperature}
					onChange={(e) => setTemperature(parseFloat(e.target.value))}
					required
				/>
			</div>

			<div className="form-group">
				<label htmlFor="maxTokens">Max Tokens:</label>
				<input
					type="number"
					id="maxTokens"
					className="form-control"
					value={maxTokens}
					onChange={(e) => setMaxTokens(parseInt(e.target.value))}
					required
				/>
			</div>

			<div className="form-group">
				<label htmlFor="prompt">Prompt:</label>
				<textarea
					id="prompt"
					rows="4"
					className="form-control"
					value={prompt}
					onChange={(e) => setPrompt(e.target.value)}
					required
				></textarea>
			</div>

			<div className="form-group">
				<label htmlFor="interveneAfter">Intervene After:</label>
				<div className="d-flex align-items-center">
					<input
						type="number"
						id="interveneAfterMinutes"
						className="form-control mr-2"
						placeholder="Minutes"
						min="0"
						value={interveneAfterMinutes}
						onChange={(e) =>
							setInterveneAfterMinutes(parseInt(e.target.value) || 0)
						}
						required
						style={{ width: '120px' }}
					/>
					<input
						type="number"
						id="interveneAfterSeconds"
						className="form-control mr-2"
						placeholder="Seconds"
						min="0"
						max="59"
						value={interveneAfterSeconds}
						onChange={(e) =>
							setInterveneAfterSeconds(parseInt(e.target.value) || 0)
						}
						required
						style={{ width: '120px' }}
					/>
				</div>
			</div>

			<div className="form-group">
				<label htmlFor="promptIdleParticipantsAfter">
					Prompt Idle Participants After:
				</label>
				<div className="d-flex align-items-center">
					<input
						type="number"
						id="promptIdleParticipantsAfterMinutes"
						className="form-control mr-2"
						placeholder="Minutes"
						min="0"
						value={promptIdleParticipantsAfterMinutes}
						onChange={(e) =>
							setPromptIdleParticipantsAfterMinutes(
								parseInt(e.target.value) || 0
							)
						}
						required
						style={{ width: '120px' }}
					/>
					<input
						type="number"
						id="promptIdleParticipantsAfterSeconds"
						className="form-control mr-2"
						placeholder="Seconds"
						min="0"
						max="59"
						value={promptIdleParticipantsAfterSeconds}
						onChange={(e) =>
							setPromptIdleParticipantsAfterSeconds(
								parseInt(e.target.value) || 0
							)
						}
						required
						style={{ width: '120px' }}
					/>
				</div>
			</div>

			<div className="form-group">
				<label htmlFor="datetime">Date/time of creation (set by system):</label>
				<input
					type="datetime-local"
					id="datetime"
					className="form-control"
					value={datetime}
					onChange={(e) => setDatetime(e.target.value)}
					required
					readOnly
				/>
			</div>

			<div className="form-group">
				<label htmlFor="privacyPolicyMessage">Privacy Policy Message:</label>
				<textarea
					id="privacyPolicyMessage"
					rows="4"
					className="form-control"
					value={privacyPolicyMessage}
					onChange={(e) => setPrivacyPolicyMessage(e.target.value)}
				></textarea>
			</div>

			<div className="form-group">
				<label htmlFor="privacyPolicyLink">Privacy Policy Link:</label>
				<input
					type="url"
					id="privacyPolicyLink"
					className="form-control"
					value={privacyPolicyLink}
					onChange={(e) => setPrivacyPolicyLink(e.target.value)}
				/>
			</div>

			<div className="form-group">
				<label htmlFor="documents">Associated Documents:</label>
				<p></p>
				<input
					type="file"
					id="documents"
					accept=".csv,.doc,.docx,.pdf,.txt,.xls,.xlsx"
					multiple
					onChange={handleFileChange}
				/>
				<p></p>
				<div id="uploaded-files">
					{associatedDocuments.map((filename, idx) => (
						<div key={idx}>
							{filename}
							<button
								className="btn btn-sm btn-link"
								type="button"
								onClick={() => handleDeleteDocument(filename)}
							>
								Remove
							</button>
						</div>
					))}
				</div>
				<p></p>
				<small>
					Note: Only .csv, .doc, .docx, .pdf, .txt, .xls, .xlsx files are
					allowed.
				</small>
			</div>

			<div className="form-group">
				<button type="submit" className="btn btn-primary">
					Submit
				</button>
				<button
					type="button"
					className="btn btn-outline-primary"
					onClick={handleReset}
				>
					Reset
				</button>
			</div>
		</form>
	);
};

export default ModerationProfileForm;
